import type { GameState } from "../engine/state/GameState.js";
import { StateSource } from "./StateSource.js";
import { RolledGameState } from "../engine/state/RolledGameState.js";
import type { Engine } from "../engine/Engine.js";
import type { PlayerType } from "../model/PlayerType.js";
import type { Roll } from "../model/dice/Roll.js";
import { WaitingForRollGameState } from "../engine/state/WaitingForRollGameState.js";
import { WaitingForMoveGameState } from "../engine/state/WaitingForMoveGameState.js";
import { MovedGameState } from "../engine/state/MovedGameState.js";
import { EndGameState } from "../engine/state/EndGameState.js";
import type { Move } from "../model/Move.js";
import { ResignedGameState } from "../engine/state/ResignedGameState.js";
import type { AbandonReason } from "../model/AbandonReason.js";
import { AbandonedGameState } from "../engine/state/AbandonedGameState.js";

/**
 * Produces game states from previous game states using the actions
 * that were taken in a game. This effectively simulates games and
 * uses saved information to fill in the gaps and as a sanity check.
 */
export class DerivedStateSource extends StateSource {
    private readonly states: GameState[];
    private stateIndex: number;

    constructor(initialState: GameState) {
        super();
        this.states = [initialState];
        this.stateIndex = 0;
    }

    getAllStates(): GameState[] {
        return this.states;
    }

    lastIndexOf(state: GameState): number {
        for (let index = this.states.length - 1; index >= 0; --index) {
            if (state.equals(this.states[index]))
                return index;
        }
        throw new Error("State could not be found");
    }

    private peekState(): GameState {
        if (this.stateIndex >= this.states.length)
            throw new Error("No available states!");
        return this.states[this.stateIndex];
    }

    private nextState(): GameState {
        if (this.stateIndex >= this.states.length)
            throw new Error("No available states!");

        const index = this.stateIndex;
        this.stateIndex += 1;
        return this.states[index];
    }

    private getCurrentState(): GameState {
        if (this.stateIndex === this.states.length) {
            return this.states[this.stateIndex - 1];
        } else {
            return this.states[this.stateIndex];
        }
    }

    private pushStates(states: GameState[]) {
        if (this.stateIndex < this.states.length)
            throw new Error("There are remaining unused states!");
        this.states.push(...states);
    }

    override createRolledState(
        rules: Engine,
        timeSinceGameStartMs: number,
        _turn: PlayerType,
        roll: Roll,
    ): RolledGameState {
        const precedingState = this.nextState();
        if (!(precedingState instanceof WaitingForRollGameState))
            throw new Error("Preceding state is not a WaitingForRollGameState");

        this.pushStates(rules.applyRoll(precedingState, timeSinceGameStartMs, roll));
        const state = this.nextState();
        if (!(state instanceof RolledGameState))
            throw new Error("The state was not a RolledGameState after applying a roll");

        return state;
    }

    override createMovedState(
        rules: Engine,
        timeSinceGameStartMs: number,
        turn: PlayerType,
        roll: Roll,
        move: Move,
    ): MovedGameState {
        // Support for implied roll states from moves.
        if (this.peekState() instanceof WaitingForRollGameState) {
            this.createRolledState(rules, timeSinceGameStartMs, turn, roll);
        }

        const precedingState = this.nextState();
        if (!(precedingState instanceof WaitingForMoveGameState))
            throw new Error("Preceding state is not a WaitingForMoveGameState");

        this.pushStates(rules.applyMove(precedingState, timeSinceGameStartMs, move));
        const state = this.nextState();
        if (!(state instanceof MovedGameState))
            throw new Error("The state was not a MovedGameState after applying a roll");

        return state;
    }

    override createWaitingForRollState(
        _rules: Engine,
        timeSinceGameStartMs: number,
        turn: PlayerType,
    ): WaitingForRollGameState {
        const state = this.getCurrentState();
        if (!(state instanceof WaitingForRollGameState))
            throw new Error("The state is not a WaitingForRollGameState");

        if (state.getTurn() !== turn) {
            throw new Error(
                "Inconsistent derivation! "
                + `Expected turn = ${turn.getName()}, `
                + `actual turn = ${state.getTurn().getName()}`,
            );
        }
        return state;
    }

    override createWaitingForMoveState(
        _rules: Engine,
        timeSinceGameStartMs: number,
        turn: PlayerType,
        roll: Roll,
    ): WaitingForMoveGameState {
        const state = this.getCurrentState();
        if (!(state instanceof WaitingForMoveGameState))
            throw new Error("The state is not a WaitingForMoveGameState");

        if (state.getTurn() !== turn) {
            throw new Error(
                "Inconsistent derivation! "
                + `Expected turn = ${turn.getName()}, `
                + `actual turn = ${state.getTurn().getName()}`,
            );
        }
        if (state.getRoll().value() !== roll.value()) {
            throw new Error(
                "Inconsistent derivation! "
                + `Expected roll value = ${roll.value()}, `
                + `actual roll value = ${state.getRoll().value()}`,
            );
        }
        return state;
    }

    override createResignedState(
        rules: Engine,
        timeSinceGameStartMs: number,
        player: PlayerType,
    ): ResignedGameState {
        const currentState = this.nextState();
        if (currentState instanceof EndGameState)
            throw new Error("Game is already finished");

        this.pushStates(rules.applyResign(currentState, timeSinceGameStartMs, player));
        const state = this.nextState();
        if (!(state instanceof ResignedGameState))
            throw new Error("The state is not a ResignedGameState");

        return state;
    }

    override createAbandonedState(
        rules: Engine,
        timeSinceGameStartMs: number,
        reason: AbandonReason,
        player: PlayerType | null,
    ): AbandonedGameState {
        const currentState = this.nextState();
        if (currentState instanceof EndGameState)
            throw new Error("Game is already finished");

        this.pushStates(rules.applyAbandon(currentState, timeSinceGameStartMs, reason, player));
        const state = this.nextState();
        if (!(state instanceof AbandonedGameState))
            throw new Error("The state is not a ResignedGameState");

        return state;
    }

    override createEndState(
        _rules: Engine,
        _timeSinceGameStartMs: number,
        winner: PlayerType | null,
    ): EndGameState {
        const state = this.getCurrentState();
        if (!(state instanceof EndGameState))
            throw new Error("The state is not a EndGameState");

        const stateWinner = (state.hasWinner() ? state.getWinner() : null);
        if (state.getWinner() !== winner) {
            throw new Error(
                "Inconsistent derivation! "
                + `Expected winner = ${winner !== null ? winner.getName() : null}, `
                + `actual winner = ${stateWinner !== null ? stateWinner.getName() : null}`,
            );
        }
        return state;
    }
}
